import axios, {AxiosError, AxiosResponse} from 'axios'
import z from 'zod'
import {LOCAL_STORAGE_USER} from '../auth'
import {ApiError} from './ApiError'
import {H3_BASE_URI} from './environment'

const ErrorResponseSchema = z.object({
    type: z.string().min(1),
    messages: z.record(z.string(), z.string()),
    stack: z.array(z.string()),
    cause: z.record(z.string().min(1), z.any()).optional()
})

export const API = axios.create({
    baseURL: H3_BASE_URI,
    maxContentLength: 30 * 1024 * 1024,
    maxBodyLength: 30 * 1024 * 1024,
    headers: {
        'Content-Type': 'application/json'
    }
})

API.interceptors.request.use(config => {
    let data

    if (
        !config.headers['authorization'] &&
        (data = localStorage.getItem(LOCAL_STORAGE_USER))
    ) {
        let user = JSON.parse(data)
        config.headers['authorization'] = `Bearer ${user.token}`
    }

    return config
})

API.interceptors.response.use(
    response => response,
    error => {
        let apiError
        // This is a client error, the request was never made. See axiosCodeToMessage() for details
        if (!error.response) {
            let stack = error.stack?.split(/\n\s+/g) ?? []
            let message = `${error.message} - ${axiosCodeToMessage(error.code ?? '')}`
            apiError = new ApiError(
                0,
                error.code ?? 'ERR',
                {0: message},
                stack,
                undefined,
                error.cause
            )
        } else {
            let r = error.response as AxiosResponse
            let data = r?.data ?? ''

            if (
                !Array.isArray(data) &&
                typeof data == 'object' &&
                data.error?.type
            ) {
                try {
                    let h3 = ErrorResponseSchema.parse(data.error)
                    apiError = new ApiError(
                        r.status ?? -1,
                        h3.type,
                        h3.messages,
                        h3.stack,
                        r,
                        h3.cause
                    )
                } catch (e) {
                    apiError = new ApiError(
                        r.status ?? -1,
                        'InvalidFormat',
                        {0: 'Malformed H3 Response'},
                        [],
                        r
                    )
                    console.debug(
                        `Failed to parse H3 error response schema: ${e} - payload '${r.data}'`
                    )
                }
            }
        }

        return Promise.reject(apiError ?? error)
    }
)

function axiosCodeToMessage(code: string) {
    switch (code) {
        case AxiosError.ERR_NETWORK:
            return 'Your internet connection appears to be down.'

        case AxiosError.ERR_BAD_OPTION_VALUE:
        case AxiosError.ERR_BAD_OPTION:
        case AxiosError.ERR_DEPRECATED:
        case AxiosError.ERR_INVALID_URL:
        case AxiosError.ERR_BAD_RESPONSE:
        case AxiosError.ERR_BAD_REQUEST:
        case AxiosError.ERR_NOT_SUPPORT:
            return 'A software error occurred. Please contact IT Support'

        case AxiosError.ECONNABORTED:
            return 'The server closed the connection while processing your request or sending the response.'

        case AxiosError.ERR_CANCELED:
            return 'Your request is taking to long to reach the server.'

        case AxiosError.ETIMEDOUT:
            return 'The server is taking too long to respond.'
    }

    return 'An unknown client error occurred. Please contact IT Support'
}
